import { ChangeEvent, FC } from 'react';
import { useState } from 'react';
import {
  Upload,
  X,
  CheckCircle,
  AlertCircle,
  Linkedin,
  FileText,
} from 'lucide-react';
import { uploadService } from '@/services/upload.service';
import { getErrorMessage } from '@/utils/error';

type DocumentCategory =
  | 'Product launch / feature update'
  | 'Case study / customer story'
  | 'Event announce / recap'
  | 'How-to / mini-guide'
  | 'Poll'
  | 'Employee shout-out / recognition'
  | 'Festivity'
  | 'Hiring / role spotlight'
  | 'Milestone / metrics'
  | 'News Commentary'
  | 'Partner/community/CSR'
  | 'Thought leadership (executive/brand)'
  | 'Leadership (executive / brand)'
  | 'Humor /relatable work-life'
  | 'Leadership-video note (60–90s)';

const CATEGORY_OPTIONS: { label: string; value: DocumentCategory }[] = [
  { label: 'Product launch / feature update', value: 'Product launch / feature update' },
  { label: 'Case study / customer story', value: 'Case study / customer story' },
  { label: 'Event announce / recap', value: 'Event announce / recap' },
  { label: 'How-to / mini-guide', value: 'How-to / mini-guide' },
  { label: 'Poll', value: 'Poll' },
  { label: 'Employee shout-out / recognition', value: 'Employee shout-out / recognition' },
  { label: 'Festivity', value: 'Festivity' },
  { label: 'Hiring / role spotlight', value: 'Hiring / role spotlight' },
  { label: 'Milestone / metrics', value: 'Milestone / metrics' },
  { label: 'News Commentary', value: 'News Commentary' },
  { label: 'Partner/community/CSR', value: 'Partner/community/CSR' },
  { label: 'Thought leadership (executive/brand)', value: 'Thought leadership (executive/brand)' },
  { label: 'Leadership (executive / brand)', value: 'Leadership (executive / brand)' },
  { label: 'Humor /relatable work-life', value: 'Humor /relatable work-life' },
  { label: 'Leadership-video note (60–90s)', value: 'Leadership-video note (60–90s)' },
];

interface DocumentUploadProps {
  onDocumentUploaded?: (fileUrl: string) => void;
  hasLinkedInToken: boolean;
  onPostToLinkedIn?: (documentId: string) => void;
}

export const DocumentUpload: FC<DocumentUploadProps> = ({
  onDocumentUploaded,
  hasLinkedInToken,
  onPostToLinkedIn,
}) => {
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState<string>('');
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [uploadedFilename, setUploadedFilename] = useState<string>('');
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<'idle' | 'success' | 'error'>(
    'idle'
  );
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');

  // ✅ NEW: category state
  const [category, setCategory] = useState<DocumentCategory>('Product launch / feature update');
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiError, setAiError] = useState('');


  const handleFileSelect = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = uploadService.validateFile(file, 'document', 5);
    if (!validation.valid) {
      setErrorMessage(validation.error || 'Invalid file');
      setUploadStatus('error');
      return;
    }

    setSelectedFile(file);
    setErrorMessage('');
    setUploadStatus('idle');
    setFileUrl('');
    setUploadedFilename('');

    // ✅ Call AI summary endpoint and auto-fill description
    setAiError('');
    setAiGenerating(true);

    try {
      const res = await uploadService.generateDocumentSummary(file);
      const summary = (res?.summary || '').trim();

      if (summary) {
        // Auto-fill only if user hasn't typed anything yet
        setDescription((prev) => (prev.trim() ? prev : summary));
      }
    } catch (err: unknown) {
      setAiError(getErrorMessage(err));
    } finally {
      setAiGenerating(false);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !title.trim()) return;

    setUploading(true);
    setUploadStatus('idle');
    setErrorMessage('');

    try {
      // ✅ NEW: pass category into api call
      const response = await uploadService.uploadMxmDocument(
        selectedFile,
        title.trim(),
        description.trim(),
        category
      );

      setFileUrl(response.url || '');
      setDocumentId(response.mxmUpload?.id || null);
      setUploadedFilename(response.filename || selectedFile.name);
      setUploadStatus('success');

      onDocumentUploaded?.(response.url || '');
    } catch (error: unknown) {
      console.error('Upload failed:', error);
      setUploadStatus('error');
      setErrorMessage(getErrorMessage(error));
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = () => {
    if (!fileUrl) return;

    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = uploadedFilename || selectedFile?.name || 'document';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handlePostToLinkedIn = () => {
    if (!documentId) return;
    onPostToLinkedIn?.(documentId);
  };

  const handleClear = () => {
    setSelectedFile(null);
    setFileUrl('');
    setUploadedFilename('');
    setUploadStatus('idle');
    setErrorMessage('');
    setDocumentId('');
    setAiGenerating(false);
    setAiError('');
  };

  const isUploaded = uploadStatus === 'success' && !!fileUrl;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center gap-2 mb-4">
        <Upload className="w-5 h-5 text-gray-600" />
        <h3 className="text-lg font-semibold text-gray-900">Upload Documents</h3>
      </div>

      <div className="space-y-4">
        {/* Title */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Enter Title..."
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={uploading}
          />
        </div>

        {/* ✅ NEW: Category dropdown */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Category
          </label>
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value as DocumentCategory)}
            className="w-full px-3 py-3 border border-gray-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={uploading}
          >
            {CATEGORY_OPTIONS.slice(0, 5).map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>


        {/* Description */}
        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Enter Description..."
            rows={4}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            disabled={uploading || aiGenerating}
          />

          {aiGenerating && (
            <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-gray-300 border-t-gray-700" />
              <span>Generating description...</span>
            </div>
          )}

          {aiError && (
            <p className="text-xs text-red-600 mt-2">{aiError}</p>
          )}
        </div>


        {/* File Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Select Document
          </label>
          <div className="flex items-center gap-3">
            <label className="flex-1 cursor-pointer">
              <input
                type="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.txt,.xls,.xlsx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation"
                onChange={handleFileSelect}
                className="hidden"
                disabled={uploading}
              />
              <div className="flex items-center justify-center px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg hover:border-gray-400 transition-colors">
                <span className="text-sm text-gray-600">
                  {selectedFile ? selectedFile.name : 'Choose document file...'}
                </span>
              </div>
            </label>

            {selectedFile && (
              <button
                onClick={handleClear}
                className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer disabled:cursor-not-allowed"
                disabled={uploading}
                type="button"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>

        {/* Document Info */}
        {selectedFile && (
          <div className="flex items-center gap-2 text-sm text-gray-700">
            <FileText className="w-4 h-4 text-gray-500" />
            <div className="flex flex-col">
              <span className="font-medium break-all">
                {uploadedFilename || selectedFile.name}
              </span>
              <span className="text-xs text-gray-500">
                {(selectedFile.size / (1024 * 1024)).toFixed(2)} MB
              </span>
            </div>
          </div>
        )}

        {/* Upload Status */}
        {uploadStatus === 'success' && (
          <div className="flex items-center gap-2 text-green-600 text-sm">
            <CheckCircle className="w-4 h-4" />
            <span>Document uploaded successfully!</span>
          </div>
        )}

        {uploadStatus === 'error' && (
          <div className="flex items-center gap-2 text-red-600 text-sm">
            <AlertCircle className="w-4 h-4" />
            <span>{errorMessage || 'Upload failed'}</span>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-3">
          {!isUploaded ? (
            <button
              onClick={handleUpload}
              disabled={!selectedFile || !title.trim() || uploading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 cursor-pointer disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              type="button"
            >
              {uploading ? 'Uploading...' : 'Upload Document'}
            </button>
          ) : (
            <button
              onClick={handleDownload}
              disabled={uploading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:bg-gray-300 cursor-pointer disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
              type="button"
            >
              Download Document
            </button>
          )}

          {fileUrl && (
            <button
              onClick={handlePostToLinkedIn}
              disabled={uploading}
              className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#0077b5] rounded-md hover:bg-[#005885] disabled:bg-gray-300 cursor-pointer disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0077b5] flex items-center justify-center gap-2"
              type="button"
            >
              <Linkedin className="w-4 h-4" />
              <span>Post to LinkedIn</span>
            </button>
          )}
        </div>

        {/* LinkedIn Token Status */}
        <div className="text-xs text-gray-500 pt-2 border-t border-gray-200">
          LinkedIn Status:{' '}
          {hasLinkedInToken ? (
            <span className="text-green-600 font-medium">Connected</span>
          ) : (
            <span className="text-orange-600 font-medium">Not Connected</span>
          )}
        </div>
      </div>
    </div>
  );
};
